import * as React from 'react';

import {
  CurrencyCode,
  InventoryMovementStatus,
  InventoryMovementType,
} from '@rematter/frontend-lib-api-client';
import {
  Badge,
  DataGrid,
  Pagination,
  type PaginationProps,
  Content,
  Footer,
  type DataGridColumn,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideArrowDownRight,
  LucideArrowUpRight,
  LucideArrowLeftRight,
  LucideRotateCcw,
} from 'lucide-react';

import { MovementHistoryRecord } from '../../_graphql/queryInventoryMovementHistory';
import * as state from '../../_state/inventoryMovementHistory.state';
import { useInventoryUnit } from '../../hooks/useInventoryUnit';
import { useTranslations } from '../../i18n/useTranslations';

import { MovementActionMenu } from './MovementActionMenu';
import { OriginatorLink } from './OriginatorLink';

import { getLocale } from '~/shared/utils/locale';

const formatCurrency = (
  amount: number,
  currencyCode: CurrencyCode | string,
): string | null => {
  if (currencyCode === 'NEUTRAL') {
    return null;
  }
  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currencyCode,
  }).format(amount);
};

const MOVEMENT_TYPE_ICONS: Record<InventoryMovementType, React.ElementType> = {
  In: LucideArrowDownRight,
  Out: LucideArrowUpRight,
  Internal: LucideArrowLeftRight,
  Reset: LucideRotateCcw,
};

const MOVEMENT_TYPE_COLORS: Record<InventoryMovementType, string> = {
  In: 'text-success',
  Out: 'text-failure',
  Internal: 'text-primary',
  Reset: 'text-warning',
};

const STATUS_BADGE_COLOR: Record<
  InventoryMovementStatus,
  'neutral' | 'success' | 'warning' | 'failure'
> = {
  Pending: 'warning',
  InTransit: 'neutral',
  Completed: 'success',
  Canceled: 'failure',
};

export const MovementHistoryTableContent = () => {
  const t = useTranslations();
  const { formatQuantityValue } = useInventoryUnit();

  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const canShowRunningTotals = useAtomValue(state.$canShowRunningTotals);

  const setAllocateDialogOpen = useSetAtom(state.$allocateDialogOpen);
  const setSelectedMovementId = useSetAtom(
    state.$selectedMovementForAllocation,
  );

  const { data, isFetching, loadingState } = useAtomValue(
    state.$remoteInventoryMovementHistory,
  );

  const movements = data?.movements ?? [];
  const total = data?.totalCount ?? 0;

  const handleAllocateToLot = React.useCallback(
    (movementId: string, facilityId: string) => {
      setSelectedMovementId({ movementId, facilityId });
      setAllocateDialogOpen(true);
    },
    [setSelectedMovementId, setAllocateDialogOpen],
  );

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const columns: DataGridColumn<MovementHistoryRecord>[] = React.useMemo(() => {
    const baseColumns: DataGridColumn<MovementHistoryRecord>[] = [
      {
        key: 'date',
        header: t.movementHistoryView.table.columns.date,
        width: 190,
        cell: ({ row }) => {
          const dateStr = row.occurredAt?.replace(/\[.*\]$/, '') || '';
          const date = new Date(dateStr);
          return (
            <span className="text-sm">
              {isNaN(date.getTime())
                ? '-'
                : date.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
            </span>
          );
        },
      },
      {
        key: 'facility',
        header: t.movementHistoryView.facilityBreakdown.facility,
        width: 140,
        cell: ({ row }) => (
          <span className="text-sm">{row.facilityName || '\u2014'}</span>
        ),
      },
      {
        key: 'type',
        header: t.movementHistoryView.table.columns.type,
        width: 110,
        cell: ({ row }) => {
          const Icon = MOVEMENT_TYPE_ICONS[row.movementType];
          const color = MOVEMENT_TYPE_COLORS[row.movementType];
          return (
            <div className={cn('flex items-center gap-2', color)}>
              <Icon className="size-4" />
              <span>
                {t.movementHistoryView.movementTypes[row.movementType]}
              </span>
            </div>
          );
        },
      },
      {
        key: 'transaction',
        header: t.movementHistoryView.table.columns.transaction,
        cell: ({ row }) => (
          <span className="text-sm">
            {t.movementHistoryView.transactionTypes[row.originTransactionType]}
          </span>
        ),
      },
      {
        key: 'quantity',
        header: t.movementHistoryView.table.columns.quantity,
        cell: ({ row }) => {
          const isPositive = row.movementType === 'In';
          const isNegative = row.movementType === 'Out';
          return (
            <span
              className={cn(
                'font-medium',
                isPositive && 'text-success',
                isNegative && 'text-failure',
              )}
            >
              {isPositive ? '+' : isNegative ? '-' : ''}
              {formatQuantityValue(row.quantity, row.quantityUnit)}
            </span>
          );
        },
      },
      {
        key: 'cost',
        header: t.movementHistoryView.table.columns.cost,
        cell: ({ row }) => {
          const cost = row.calculatedMovementValue;
          if (!cost) {
            return <span className="text-neutral/40">—</span>;
          }
          const formatted = formatCurrency(cost.amount, cost.currencyCode);
          if (!formatted) {
            return <span className="text-neutral/40">—</span>;
          }
          const isPositive = row.movementType === 'In';
          const isNegative = row.movementType === 'Out';
          return (
            <span
              className={cn(
                'text-sm tabular-nums',
                isPositive && 'text-success',
                isNegative && 'text-failure',
              )}
            >
              {isPositive && '+'}
              {formatted}
            </span>
          );
        },
      },
    ];

    baseColumns.push(
      {
        key: 'status',
        header: t.movementHistoryView.table.columns.status,
        cell: ({ row }) => (
          <Badge color={STATUS_BADGE_COLOR[row.status]}>
            {t.movementHistoryView.statusTypes[row.status]}
          </Badge>
        ),
      },
      {
        key: 'originator',
        header: t.movementHistoryView.table.columns.originator,
        cell: ({ row }) => (
          <OriginatorLink
            originTransactionType={row.originTransactionType}
            originTransactionItemId={row.originTransactionItemId}
          />
        ),
      },
    );

    if (canShowRunningTotals) {
      baseColumns.push(
        {
          key: 'runningQuantity',
          header: t.movementHistoryView.table.columns.runningQuantity,
          cell: ({ row }) => (
            <span className="font-medium tabular-nums">
              {formatQuantityValue(
                row.runningQuantity,
                row.runningQuantityUnit,
              )}
            </span>
          ),
        },
        {
          key: 'runningValue',
          header: t.movementHistoryView.table.columns.runningValue,
          cell: ({ row }) => {
            if (!row.runningValue) {
              return <span className="text-neutral/40">—</span>;
            }
            const formatted = formatCurrency(
              row.runningValue.amount,
              row.runningValue.currencyCode,
            );
            if (!formatted) {
              return <span className="text-neutral/40">—</span>;
            }
            return <span className="text-sm tabular-nums">{formatted}</span>;
          },
        },
        {
          key: 'runningWac',
          header: t.movementHistoryView.table.columns.runningWac,
          cell: ({ row }) => {
            if (!row.runningWac) {
              return <span className="text-neutral/40">—</span>;
            }
            const formatted = formatCurrency(
              row.runningWac.amount,
              row.runningWac.currencyCode,
            );
            if (!formatted) {
              return <span className="text-neutral/40">—</span>;
            }
            return <span className="text-sm tabular-nums">{formatted}</span>;
          },
        },
      );
    }

    baseColumns.push({
      key: 'actions',
      header: '',
      width: 60,
      pinned: 'right',
      cell: ({ row }) => (
        <MovementActionMenu
          movementId={row.id}
          status={row.status}
          facilityId={row.facilityId}
          onAllocateToLot={handleAllocateToLot}
        />
      ),
    });

    return baseColumns;
  }, [t, formatQuantityValue, canShowRunningTotals, handleAllocateToLot]);

  const emptyPanelProps = {
    title: t.movementHistoryView.emptyState.title,
    description: <span>{t.movementHistoryView.emptyState.subtitle}</span>,
    centered: true,
  };

  return (
    <div className="@MovementHistoryTableContent flex h-full flex-col">
      <Content className="h-full p-4.5">
        <DataGrid<MovementHistoryRecord>
          isContained
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={movements}
          rowKey="id"
          aria-label={t.movementHistoryView.table.aria}
          selectionMode="none"
          className="w-full text-sm"
          fullWidth
          emptyPanelProps={emptyPanelProps}
        />
      </Content>

      {total > limit && (
        <Footer>
          <Pagination
            limit={limit}
            offset={offset}
            total={total}
            onChange={handlePaginationChange}
          />
        </Footer>
      )}
    </div>
  );
};
